import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categoriesService } from "../../../services/categoriesService";
import { AdminLayout } from "../../layout/AdminLayout";
import { DataTable } from "../../../components/DataTable";
import { toast } from "react-toastify";
import { Edit, Trash2, Plus } from "lucide-react";

export const CategoriesList = () => {
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState("");
	const [sortBy, setSortBy] = useState("name");
	const [sortOrder, setSortOrder] = useState("asc");

	useEffect(() => {
		loadCategories();
	}, [page, pageSize, search, sortBy, sortOrder]);

	const loadCategories = async () => {
		try {
			setLoading(true);
			const data = await categoriesService.getAll(
				page,
				pageSize,
				search,
				sortBy,
				sortOrder,
			);
			setCategories(data.data || []);
			setTotal(data.total || 0);
		} catch (error) {
			console.error("Error loading categories:", error);
			toast.error("Failed to load categories");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		if (!globalThis.confirm("Are you sure?")) return;

		try {
			await categoriesService.delete(id);
			toast.success("Category deleted");
			loadCategories();
		} catch (error) {
			console.error("Error deleting category:", error);
			toast.error("Failed to delete category");
		}
	};

	const columns = [
		{
			key: "name",
			label: "Name",
			sortable: true,
			width: "24%",
		},
		{
			key: "description",
			label: "Description",
			sortable: false,
			width: "34%",
			render: (row) => row.description || "-",
		},
		{
			key: "skills",
			label: "Skills",
			sortable: false,
			width: "24%",
			render: (row) => {
				const skills = Array.isArray(row.skills) ? row.skills : [];
				const names = skills
					.map((skill) =>
						typeof skill === "string" ? skill : skill?.name || "",
					)
					.filter(Boolean);

				if (names.length === 0) {
					return <span className="text-gray-500">No skills</span>;
				}

				const preview = names.slice(0, 3);
				const remaining = names.length - 3;

				return (
					<div className="flex flex-wrap gap-1" title={names.join(", ")}>
						{preview.map((name) => (
							<span
								key={name}
								className="inline-flex items-center rounded-full border border-gray-300 bg-gray-200/60 px-2 py-0.5 text-xs text-gray-700"
							>
								{name}
							</span>
						))}
						{remaining > 0 && (
							<span className="inline-flex items-center rounded-full border border-gray-300 bg-gray-200/60 px-2 py-0.5 text-xs text-gray-700">
								+{remaining} more
							</span>
						)}
					</div>
				);
			},
		},
		{
			key: "actions",
			label: "Actions",
			sortable: false,
			width: "18%",
			render: (row) => (
				<div className="flex gap-2">
					<Link
						to={`/admin/categories/${row._id}`}
						className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
					>
						<Edit size={16} />
						Edit
					</Link>
					<button
						onClick={() => handleDelete(row._id)}
						className="text-red-600 hover:text-red-800 flex items-center gap-1"
					>
						<Trash2 size={16} />
						Delete
					</button>
				</div>
			),
		},
	];

	return (
		<AdminLayout>
			<div className="p-6">
				<div className="flex justify-between items-center mb-6">
					<h1 className="text-3xl font-bold text-gray-800">Categories</h1>
					<Link
						to="/admin/categories/new"
						className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition"
					>
						<Plus size={20} />
						Add Category
					</Link>
				</div>

				<DataTable
					columns={columns}
					data={categories}
					loading={loading}
					totalItems={total}
					currentPage={page}
					pageSize={pageSize}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
					onSearch={setSearch}
					onSort={(column, order) => {
						setSortBy(column);
						setSortOrder(order);
					}}
					searchPlaceholder="Search by name..."
					sortBy={sortBy}
					sortOrder={sortOrder}
				/>
			</div>
		</AdminLayout>
	);
};
